import * as SelectPrimitive from "@radix-ui/react-select";
import { ChevronDown } from "lucide-react";
import { Select, SelectContent, SelectItem } from "@/components/ui/select";
import { langCode, langName } from "../model/types";

interface LanguageDropdownProps {
  languages: string[];
  currentLanguage: string;
  sourceLanguage?: string;
  onLanguageChange: (language: string) => void;
}

/** Radix reserves the empty string as "no value", but the source language is stored
 *  as "" — swap it for a sentinel on the way in and back out. */
const SOURCE_VALUE = "__source__";
const toItemValue = (language: string) => language || SOURCE_VALUE;
const fromItemValue = (value: string) => (value === SOURCE_VALUE ? "" : value);

/** Output-language picker for the active persona chip.
 *
 *  Built on the shared Radix Select rather than a hand-rolled popover: the menu
 *  renders through a portal, so it escapes the persona strip's flow instead of
 *  overlapping the tab row beneath it, and collision detection flips it when there
 *  is no room below. Focus handling, typeahead, Escape and click-outside come with
 *  the primitive. The trigger stays a compact badge, so it composes
 *  SelectPrimitive.Trigger directly instead of the full-size shared SelectTrigger. */
export function LanguageDropdown({
  languages,
  currentLanguage,
  sourceLanguage,
  onLanguageChange,
}: LanguageDropdownProps) {
  return (
    <Select
      value={toItemValue(currentLanguage)}
      onValueChange={(value) => onLanguageChange(fromItemValue(value))}
    >
      <SelectPrimitive.Trigger
        aria-label="Output language"
        title="Output language"
        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded bg-muted text-muted-foreground outline-none hover:bg-muted-foreground/20 focus-visible:ring-1 focus-visible:ring-ring transition-colors"
      >
        {langCode(currentLanguage, sourceLanguage)}
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="w-2.5 h-2.5" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectContent align="start" className="min-w-[128px]">
        {languages.map((lang) => (
          <SelectItem key={toItemValue(lang)} value={toItemValue(lang)} className="text-xs">
            <span className="font-medium">{langCode(lang, sourceLanguage)}</span>
            <span className="text-muted-foreground">{langName(lang, sourceLanguage)}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
